import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from './utils/cn';
import { CardPair } from './PlayingCard';
import { CompactChipStack } from './PokerChip';
import { User, Crown, Timer, Eye } from 'lucide-react';

const PATH_LEN = 1000;

/** Уникальный цвет ника по номеру места за столом (0 = герой / первый игрок — зелёный). */
const SEAT_NAME_COLOR_CLASSES = [
  'text-emerald-400',
  'text-sky-400',
  'text-amber-300',
  'text-violet-400',
];

function seatNameColorClass(seatColorIndex) {
  if (seatColorIndex == null || !Number.isFinite(Number(seatColorIndex))) return 'text-foreground';
  const i = Math.floor(Number(seatColorIndex));
  return SEAT_NAME_COLOR_CLASSES[((i % SEAT_NAME_COLOR_CLASSES.length) + SEAT_NAME_COLOR_CLASSES.length) % SEAT_NAME_COLOR_CLASSES.length];
}

function SeatModeIcon({ mode }) {
  if (mode === 'open') {
    return <Eye className="h-4 w-4 text-cyan-200 drop-shadow-[0_0_8px_rgba(56,189,248,0.65)]" aria-hidden />;
  }
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-4 w-4 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]"
      aria-hidden
    >
      <path
        d="M6.2 8.1V6.7a3.8 3.8 0 0 1 7.6 0v1.4h.4A1.8 1.8 0 0 1 16 9.9v6.2a1.8 1.8 0 0 1-1.8 1.8H5.8A1.8 1.8 0 0 1 4 16.1V9.9a1.8 1.8 0 0 1 1.8-1.8h.4Zm1.7 0h4.2V6.8a2.1 2.1 0 0 0-4.2 0v1.3Z"
        fill="#0a0a0a"
        stroke="#facc15"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="12.9" r="1.05" fill="#facc15" />
      <rect x="9.5" y="13.8" width="1" height="2.2" rx="0.5" fill="#facc15" />
    </svg>
  );
}

/**
 * Замкнутый контур скруглённого прямоугольника по часовой стрелке от верхней середины.
 * Координаты в пикселях viewBox (совпадают с clientWidth / clientHeight контейнера).
 */
function seatCountdownRingPath(w, h, strokeHalf, cornerR) {
  if (w < 8 || h < 8) return '';
  const inset = Math.max(strokeHalf + 0.5, 1);
  const iw = w - inset * 2;
  const ih = h - inset * 2;
  if (iw < 4 || ih < 4) return '';
  const r = Math.min(cornerR, iw / 2, ih / 2);
  const x = inset;
  const y = inset;
  const x1 = x + iw - r;
  const y1 = y + ih - r;
  const x0 = x + r;
  const y0 = y + r;
  const mx = x + iw / 2;
  return [
    `M ${mx} ${y}`,
    `L ${x1} ${y}`,
    `A ${r} ${r} 0 0 1 ${x + iw} ${y0}`,
    `L ${x + iw} ${y1}`,
    `A ${r} ${r} 0 0 1 ${x1} ${y + ih}`,
    `L ${x0} ${y + ih}`,
    `A ${r} ${r} 0 0 1 ${x} ${y1}`,
    `L ${x} ${y0}`,
    `A ${r} ${r} 0 0 1 ${x0} ${y}`,
    `L ${mx} ${y}`,
    'Z',
  ].join(' ');
}

/** Центр и радиус круга для неонового кольца таймера вокруг аватара (stackMetaVertically). */
function seatAvatarCountdownCircle(w, h, strokeHalf) {
  if (w < 8 || h < 8) return null;
  const inset = Math.max(strokeHalf + 0.5, 1);
  const r = Math.max(3, Math.min(w, h) / 2 - inset);
  return { cx: w / 2, cy: h / 2, r };
}

export function IndustrialSeat({
  name,
  chips,
  chipsDisplay,
  bet = 0,
  cards = [],
  isDealer = false,
  isCurrentTurn = false,
  isFolded = false,
  isWinner = false,
  showCards = false,
  isUser = false,
  /** Показать аватар бота из `/avatar-for-bot.png` вместо иконки User. */
  isBot = false,
  /** URL аватара игрока (онлайн-профиль); при ошибке загрузки — иконка User. */
  avatarUrl = '',
  /** Клик по аватару (профиль). Не вызывается при `suppressAvatarDragPropagation` (режим «расставить стол»). */
  onAvatarClick,
  /** В режиме перетаскивания мест — не начинать drag с аватара и не открывать профиль по клику. */
  suppressAvatarDragPropagation = false,
  /** Индекс места 0…n для уникального цвета ника; если не задан — `text-foreground`. */
  seatColorIndex = undefined,
  /** Явный Tailwind-класс цвета ника (приоритетнее `seatColorIndex`). */
  nameColorClass,
  /** Оставшиеся секунды хода (вместе с `turnTimeTotalSec` — индикатор в рамке места). */
  turnTimeLeftSec = null,
  /** Полный лимит секунд (для доли видимой неоновой обводки). */
  turnTimeTotalSec = null,
  /**
   * Доля оставшегося времени 0…1 (для коротких ходов бота по мс).
   * Если задано, обводка считается по нему; секунды в углу только при `hasSecTimer`.
   */
  turnTimeFraction = undefined,
  /** Красный перечёркивающий крест (поражение в раздаче у героя). */
  showDefeatCross = false,
  /** Тот же крест: выбыл со стека (0 фишек и т.п.). */
  showEliminatedCross = false,
  /** Узкая/низкая карточка места (например Белот). */
  compact = false,
  /** Круг аватара и иконка User на +15% к базовым размерам (белот и т.п.). */
  boostSeatAvatar = false,
  /**
   * По слоту карты: true = рубашка. Если не задан — как раньше: все карты смотрят на `showCards`.
   * Для анимации раздачи Local Seka.
   */
  cardFaceDownByIndex,
  /** Ключи слотов карт (смена = remount + CSS-анимация раздачи). */
  dealCardKeys,
  /** Ref на обёртку каждого слота карты (анимация раздачи из колоды). */
  cardSlotRefs,
  /** DOM-источник для ante-полетов (LocalGame). */
  anteSourceRef,
  /** Показ карты по слоту; false = пустой слот до раздачи (Local Seka). */
  showCardByIndex,
  /** Короткая вторичная подпись под местом (например «Бот втемную»). */
  footerLabel,
  /** Клик по текстовому футеру (например "🃏 N"). */
  onFooterLabelClick,
  /** Ref на DOM-элемент footerLabel (для анимаций прилёта). */
  footerLabelRef,
  /** Короткий индикатор над блоком места (например «Бот · в темную»). */
  modeIndicatorLabel,
  /** Кастомный блок вместо подписи+иконки (например «река» ходов торгов). */
  modeIndicatorSlot = null,
  /** open|dark — режим для иконки индикатора. */
  modeIndicatorState = 'dark',
  /** Бейдж последнего действия рядом с ником (передаётся из LocalGame). */
  nameActionBadge,
  /** full|minimal — минималистичный футер для мест у колоды. */
  footerMode = 'full',
  /**
   * Белот / узкий мобайл: бейджи и таймер секунд над аватаром, ник и строка фишек под аватаром
   * (уже́ минимальная ширина карточки).
   */
  stackMetaVertically = false,
  children,
}) {
  const trimmedAvatar = typeof avatarUrl === 'string' ? avatarUrl.trim() : '';
  const [humanAvatarBroken, setHumanAvatarBroken] = useState(false);
  useEffect(() => {
    setHumanAvatarBroken(false);
  }, [trimmedAvatar]);
  const showHumanAvatarImg = !isBot && Boolean(trimmedAvatar) && !humanAvatarBroken;

  const showStrikeCross = Boolean(showDefeatCross || showEliminatedCross);
  const hasSecTimer =
    isCurrentTurn &&
    turnTimeTotalSec != null &&
    turnTimeTotalSec > 0 &&
    turnTimeLeftSec != null &&
    turnTimeLeftSec > 0;
  /** Ноль = время вышло; не держим «пустое» кольцо и 50 Гц ререндеры соперника. */
  const hasFracTimer =
    isCurrentTurn &&
    turnTimeFraction != null &&
    Number.isFinite(turnTimeFraction) &&
    turnTimeFraction > 0;
  const showSeatCountdown = hasSecTimer || hasFracTimer;
  const stackV = Boolean(stackMetaVertically);
  /** Кольцо обводки хода — по периметру аватара, а не всей карточки (узкий белот). */
  const ringOnAvatar = stackV && showSeatCountdown;

  /** Серверный таймер уже на нуле, а `isCurrentTurn` ещё true — без этого крутится fallback-иконка Timer. */
  const turnTimerExhausted =
    isCurrentTurn &&
    ((turnTimeTotalSec != null &&
      turnTimeTotalSec > 0 &&
      turnTimeLeftSec != null &&
      turnTimeLeftSec <= 0) ||
      (turnTimeFraction != null &&
        Number.isFinite(turnTimeFraction) &&
        turnTimeFraction <= 0));

  const turnProgress = hasFracTimer
    ? Math.min(1, Math.max(0, turnTimeFraction))
    : hasSecTimer
      ? Math.min(1, Math.max(0, turnTimeLeftSec / turnTimeTotalSec))
      : 0;

  const boxRef = useRef(null);
  const avatarRingRef = useRef(null);
  const [{ w, h }, setSize] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    if (!showSeatCountdown) return undefined;
    const el = ringOnAvatar ? avatarRingRef.current : boxRef.current;
    if (!el) return undefined;

    const read = () => {
      const rect = el.getBoundingClientRect();
      setSize({
        w: Math.max(0, Math.round(rect.width)),
        h: Math.max(0, Math.round(rect.height)),
      });
    };

    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showSeatCountdown, ringOnAvatar]);

  const strokeW = 2.5;
  const ringPath = useMemo(
    () => (ringOnAvatar ? '' : seatCountdownRingPath(w, h, strokeW / 2, 8)),
    [ringOnAvatar, w, h]
  );
  const avatarRingCircle = useMemo(
    () => (ringOnAvatar ? seatAvatarCountdownCircle(w, h, strokeW / 2) : null),
    [ringOnAvatar, w, h]
  );

  const stackLine =
    chips != null && Number.isFinite(Number(chips))
      ? `${Number(chips).toLocaleString('ru-RU')} SEKA`
      : chipsDisplay;
  const minimalFooter = footerMode === 'minimal';
  const showFooterLabel = Boolean(footerLabel) && !minimalFooter;
  const showFooterBet = bet > 0 && !minimalFooter;
  const showFooter = showFooterLabel || showFooterBet;
  const hasFooterChildren = children != null && children !== false;
  /** Команда + «🃏 N» в одну строку (армянский стол и т.п.). */
  const footerInlineWithChildren = hasFooterChildren && showFooter;
  const showModeSlot = Boolean(modeIndicatorSlot);
  const showLegacyModeIndicator = Boolean(modeIndicatorLabel) && !showModeSlot;
  const showNameActionBadge = Boolean(nameActionBadge);
  const showBadgeStrip =
    stackV && (showNameActionBadge || isDealer || isWinner || hasSecTimer);

  const avBoost = Boolean(boostSeatAvatar);
  const avatarCircleClass = cn(
    compact && !isBot && (avBoost ? 'h-[2.0125rem] w-[2.0125rem]' : 'h-7 w-7'),
    compact && isBot && (avBoost ? 'h-[2.5875rem] w-[2.5875rem]' : 'h-9 w-9'),
    !compact && (avBoost ? 'h-[2.3rem] w-[2.3rem]' : 'h-8 w-8'),
  );
  const userFallbackIconClass = cn(
    'text-muted-foreground',
    compact ? (avBoost ? 'h-4 w-4' : 'h-3.5 w-3.5') : avBoost ? 'h-[1.15rem] w-[1.15rem]' : 'h-4 w-4',
  );

  const avatarMedia = isBot ? (
    <img
      src="/avatar-for-bot.png"
      alt=""
      width={128}
      height={128}
      decoding="async"
      draggable={false}
      className={cn(
        'h-full w-full min-h-0 min-w-0 object-cover object-center [transform:translateZ(0)]',
        'contrast-[1.04] saturate-[1.06] [image-rendering:auto]'
      )}
    />
  ) : showHumanAvatarImg ? (
    <img
      src={trimmedAvatar}
      alt=""
      width={128}
      height={128}
      decoding="async"
      draggable={false}
      onError={() => setHumanAvatarBroken(true)}
      className="h-full w-full min-h-0 min-w-0 object-cover object-center [transform:translateZ(0)]"
    />
  ) : (
    <User className={userFallbackIconClass} />
  );

  const avatarNode =
    typeof onAvatarClick === 'function' ? (
      <button
        type="button"
        aria-label="Открыть профиль"
        title="Профиль"
        className={cn(
          'relative flex shrink-0 items-center justify-center overflow-hidden rounded-full border bg-secondary shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]',
          'cursor-pointer transition hover:ring-2 hover:ring-cyan-400/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60',
          avatarCircleClass,
          isWinner && 'border-neon-gold',
          !isWinner &&
            (isCurrentTurn && !showSeatCountdown && !turnTimerExhausted
              ? 'border-neon-cyan'
              : 'border-border')
        )}
        onPointerDown={(e) => {
          if (suppressAvatarDragPropagation) e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (suppressAvatarDragPropagation) return;
          onAvatarClick(e);
        }}
      >
        {avatarMedia}
      </button>
    ) : (
      <div
        className={cn(
          'relative flex shrink-0 items-center justify-center overflow-hidden rounded-full border bg-secondary shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]',
          avatarCircleClass,
          isWinner && 'border-neon-gold',
          !isWinner &&
            (isCurrentTurn && !showSeatCountdown && !turnTimerExhausted
              ? 'border-neon-cyan'
              : 'border-border')
        )}
      >
        {avatarMedia}
      </div>
    );

  return (
    <div className={cn('flex flex-col items-center', compact ? 'gap-0.5' : 'gap-1', isFolded && 'opacity-50')}>
      {showModeSlot && (
        <div className="relative z-[6] mb-0.5 flex w-full min-w-0 max-w-[min(100%,8rem)] justify-center sm:max-w-[8.25rem]">
          {modeIndicatorSlot}
        </div>
      )}
      {showLegacyModeIndicator && (
        <div
          className={cn(
            'mb-0.5 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide',
            isCurrentTurn
              ? 'border-neon-cyan/60 bg-cyan-500/20 text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.35)]'
              : modeIndicatorState === 'open'
                ? 'border-cyan-300/55 bg-cyan-500/15 text-cyan-100 shadow-[0_0_14px_rgba(56,189,248,0.25)]'
                : 'border-amber-300/60 bg-amber-500/18 text-amber-100 shadow-[0_0_14px_rgba(251,191,36,0.22)]'
          )}
          title={typeof modeIndicatorLabel === 'string' ? modeIndicatorLabel : undefined}
        >
          <SeatModeIcon mode={modeIndicatorState} />
          {modeIndicatorLabel}
        </div>
      )}
      <div
        ref={boxRef}
        className={cn(
          'relative flex rounded-lg border bg-card/95',
          stackV
            ? cn(
                'flex-col items-stretch',
                compact ? 'min-w-0 max-w-[min(100%,5.75rem)] gap-1' : 'min-w-0 max-w-[min(100%,6.5rem)] gap-1.5',
              )
            : cn(
                'items-center',
                compact
                  ? cn(avBoost ? 'min-w-[100px]' : 'min-w-[92px]', 'gap-1.5')
                  : cn(avBoost ? 'min-w-[108px]' : 'min-w-[100px]', 'gap-2'),
              ),
          showSeatCountdown
            ? cn(
                ringOnAvatar ? 'overflow-hidden border-border/50' : 'overflow-visible border-border/50',
                compact ? 'p-1.5' : 'p-2',
              )
            : cn('overflow-hidden', compact ? 'p-1.5' : 'p-2'),
          isCurrentTurn &&
            !showSeatCountdown &&
            !turnTimerExhausted &&
            cn(
              'z-[2] border-2 border-cyan-200/90 bg-card/98',
              'shadow-[0_0_0_2px_rgba(0,25,22,0.75),0_0_28px_rgba(34,211,238,0.55),0_10px_36px_rgba(0,0,0,0.35)]',
            ),
          isWinner && 'border-neon-gold shadow-[0_0_20px_rgba(255,215,0,0.5)]',
          !isCurrentTurn && !isWinner && !showSeatCountdown && 'border-border',
          isUser && !stackV && 'ring-2 ring-neon-gold/60'
        )}
      >
        {showSeatCountdown && !ringOnAvatar && w > 0 && h > 0 && ringPath && (
          <svg
            className="pointer-events-none absolute inset-0 z-0 h-full w-full"
            viewBox={`0 0 ${w} ${h}`}
            width="100%"
            height="100%"
            preserveAspectRatio="none"
            aria-hidden
          >
            <path
              d={ringPath}
              fill="none"
              stroke="var(--pt-neon-cyan)"
              strokeWidth={strokeW}
              strokeLinecap="round"
              strokeLinejoin="round"
              pathLength={PATH_LEN}
              strokeDasharray={PATH_LEN}
              strokeDashoffset={PATH_LEN * (1 - turnProgress)}
              style={{
                transition: hasFracTimer
                  ? 'stroke-dashoffset 0.05s linear'
                  : 'stroke-dashoffset 0.35s linear',
                filter: 'drop-shadow(0 0 3px rgba(0, 220, 230, 0.4))',
              }}
            />
          </svg>
        )}

        <div
          className={cn(
            'relative z-[1] w-full min-w-0',
            stackV
              ? 'flex flex-col items-center gap-1'
              : cn('flex items-center', compact ? 'gap-1.5' : 'gap-2'),
            showSeatCountdown && 'rounded-md'
          )}
        >
          {stackV ? (
            <>
              {showBadgeStrip ? (
                <div className="flex w-full min-w-0 flex-wrap items-center justify-center gap-0.5 px-0.5">
                  {showNameActionBadge ? <span className="shrink-0">{nameActionBadge}</span> : null}
                  {isDealer && (
                    <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-neon-gold">
                      <span className="text-[8px] font-bold text-black">D</span>
                    </div>
                  )}
                  {isWinner && <Crown className="h-3 w-3 shrink-0 text-neon-gold" />}
                  {hasSecTimer && (
                    <span
                      className={cn(
                        'shrink-0 font-mono font-bold tabular-nums text-neon-cyan',
                        compact ? 'text-[9px]' : 'text-[10px]'
                      )}
                      aria-live="polite"
                      title={`Осталось ${turnTimeLeftSec} с`}
                    >
                      {turnTimeLeftSec}
                    </span>
                  )}
                </div>
              ) : null}
              <div className="flex shrink-0 justify-center">
                <div ref={avatarRingRef} className="relative inline-flex shrink-0">
                  <div className="relative z-0">{avatarNode}</div>
                  {ringOnAvatar && avatarRingCircle && w > 0 && h > 0 && (
                    <svg
                      className="pointer-events-none absolute inset-0 z-[2] h-full w-full overflow-visible"
                      viewBox={`0 0 ${w} ${h}`}
                      width="100%"
                      height="100%"
                      aria-hidden
                    >
                      <g
                        transform={`translate(${avatarRingCircle.cx} ${avatarRingCircle.cy}) rotate(-90)`}
                      >
                        <circle
                          cx={0}
                          cy={0}
                          r={avatarRingCircle.r}
                          fill="none"
                          stroke="var(--pt-neon-cyan)"
                          strokeWidth={strokeW}
                          strokeLinecap="round"
                          pathLength={PATH_LEN}
                          strokeDasharray={PATH_LEN}
                          strokeDashoffset={PATH_LEN * (1 - turnProgress)}
                          style={{
                            transition: hasFracTimer
                              ? 'stroke-dashoffset 0.05s linear'
                              : 'stroke-dashoffset 0.35s linear',
                            filter: 'drop-shadow(0 0 3px rgba(0, 220, 230, 0.4))',
                          }}
                        />
                      </g>
                    </svg>
                  )}
                </div>
              </div>
              <span
                className={cn(
                  'w-full min-w-0 max-w-full truncate text-center font-medium',
                  nameColorClass || seatNameColorClass(seatColorIndex),
                  compact ? 'text-[11px] leading-tight' : 'text-xs'
                )}
              >
                {name}
              </span>
              {stackLine != null && stackLine !== '' && (
                <span
                  ref={anteSourceRef}
                  className={cn(
                    'w-full min-w-0 max-w-full truncate text-center font-mono text-neon-cyan',
                    compact ? 'text-[9px]' : 'text-[10px]'
                  )}
                >
                  {stackLine}
                </span>
              )}
            </>
          ) : (
            <>
              {avatarNode}
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex min-w-0 items-center gap-1">
                  <span
                    className={cn(
                      'max-w-[52px] truncate font-medium',
                      nameColorClass || seatNameColorClass(seatColorIndex),
                      compact ? 'text-[11px] leading-tight' : 'text-xs'
                    )}
                  >
                    {name}
                  </span>
                  {showNameActionBadge ? <span className="shrink-0">{nameActionBadge}</span> : null}
                  {isDealer && (
                    <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-neon-gold">
                      <span className="text-[8px] font-bold text-black">D</span>
                    </div>
                  )}
                  {isWinner && <Crown className="h-3 w-3 shrink-0 text-neon-gold" />}
                  {hasSecTimer && (
                    <span
                      className={cn(
                        'ml-auto shrink-0 font-mono font-bold tabular-nums text-neon-cyan',
                        compact ? 'text-[9px]' : 'text-[10px]'
                      )}
                      aria-live="polite"
                      title={`Осталось ${turnTimeLeftSec} с`}
                    >
                      {turnTimeLeftSec}
                    </span>
                  )}
                </div>
                {stackLine != null && stackLine !== '' && (
                  <span
                    ref={anteSourceRef}
                    className={cn('truncate font-mono text-neon-cyan', compact ? 'text-[9px]' : 'text-[10px]')}
                  >
                    {stackLine}
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {isCurrentTurn && !showSeatCountdown && !turnTimerExhausted && (
          <div className="absolute -right-1 -top-1 z-[2]">
            <Timer
              className={cn('text-neon-cyan animate-spin', compact ? 'h-2.5 w-2.5' : 'h-3 w-3')}
              style={{ animationDuration: '2s' }}
            />
          </div>
        )}

        {showStrikeCross && (
          <div
            className="pointer-events-none absolute inset-0 z-[5] overflow-hidden rounded-[inherit]"
            aria-hidden
          >
            <div className="absolute left-1/2 top-1/2 h-[2.5px] w-[155%] origin-center rounded-full bg-red-600 pt-seat-defeat-bar-neg shadow-[0_0_10px_rgba(220,38,38,0.95)]" />
            <div className="absolute left-1/2 top-1/2 h-[2.5px] w-[155%] origin-center rounded-full bg-red-600 pt-seat-defeat-bar-pos shadow-[0_0_10px_rgba(220,38,38,0.95)]" />
          </div>
        )}
      </div>

      {cards.length > 0 && (
        <div className="-mt-1 flex w-full min-w-0 max-w-full justify-center pb-1.5 pt-0.5">
          <CardPair
            cards={cards}
            faceDown={cardFaceDownByIndex ? false : !showCards}
            faceDownByIndex={cardFaceDownByIndex}
            cardKeys={dealCardKeys}
            slotRefs={cardSlotRefs}
            showCardByIndex={showCardByIndex}
            size={isUser ? 'sm' : 'xs'}
          />
        </div>
      )}

      {(showFooter || hasFooterChildren) && (
        <div
          className={cn(
            'mt-0.5 flex w-full min-w-0 items-center justify-center gap-1',
            compact && 'gap-0.5',
            footerInlineWithChildren && 'flex-nowrap',
          )}
        >
          {footerInlineWithChildren ? (
            <>
              <div className="flex min-w-0 shrink items-center justify-center">{children}</div>
              {showFooterLabel &&
                (typeof onFooterLabelClick === 'function' ? (
                  <button
                    type="button"
                    onClick={onFooterLabelClick}
                    ref={footerLabelRef}
                    className={cn(
                      'shrink-0 truncate rounded border border-cyan-400/35 bg-cyan-500/10 px-1 py-[1px] text-[9px] font-medium text-cyan-100 transition hover:bg-cyan-500/18 hover:text-white',
                      compact && 'max-w-[56px] text-[8px]',
                    )}
                    title={footerLabel}
                  >
                    {footerLabel}
                  </button>
                ) : (
                  <span
                    ref={footerLabelRef}
                    className={cn(
                      'shrink-0 truncate text-[9px] font-medium text-muted-foreground',
                      compact && 'max-w-[56px] text-[8px]',
                    )}
                    title={footerLabel}
                  >
                    {footerLabel}
                  </span>
                ))}
              {showFooterBet && <CompactChipStack total={bet} className={cn(compact && 'scale-90')} />}
            </>
          ) : (
            <>
              {showFooter && (
                <>
                  {showFooterLabel &&
                    (typeof onFooterLabelClick === 'function' ? (
                      <button
                        type="button"
                        onClick={onFooterLabelClick}
                        ref={footerLabelRef}
                        className={cn(
                          'truncate rounded border border-cyan-400/35 bg-cyan-500/10 px-1 py-[1px] text-[9px] font-medium text-cyan-100 transition hover:bg-cyan-500/18 hover:text-white',
                          compact && 'max-w-[56px] text-[8px]',
                        )}
                        title={footerLabel}
                      >
                        {footerLabel}
                      </button>
                    ) : (
                      <span
                        ref={footerLabelRef}
                        className={cn(
                          'truncate text-[9px] font-medium text-muted-foreground',
                          compact && 'max-w-[56px] text-[8px]',
                        )}
                        title={footerLabel}
                      >
                        {footerLabel}
                      </span>
                    ))}
                  {showFooterBet && <CompactChipStack total={bet} className={cn(compact && 'scale-90')} />}
                </>
              )}
              {hasFooterChildren && !showFooter ? children : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}
